import { useState, useEffect, useMemo, useRef } from 'react';
import { Film, Tv, Plus, Search, Check, Clock, Users, ArrowLeft, Loader2, CalendarDays, ChevronDown, ChevronUp, Trash2, Flag, RefreshCw, Upload, Ban, ArrowUp, ArrowDown, X } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFilmTVData } from '../hooks/useFilmTVData';
import { useTMDB } from '../hooks/useTMDB';

const TabButton = ({ active, onClick, icon: Icon, label, iconColor }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors border-b-2 ${
      active ? 'border-zinc-900 text-zinc-900' : 'border-transparent text-zinc-400 hover:text-zinc-600'
    }`}
  >
    <Icon size={14} className={iconColor || ''} />
    {label}
  </button>
);

export default function FilmTVTracker() {
  const { authUser } = useAuth();
  const uid = authUser?.uid;
  const { 
    movies, tvShows, creators, loading, 
    addMovie, updateMovie, deleteMovie, 
    addTvShow, updateTvShow, deleteTvShow, 
    addCreator, updateCreator, deleteCreator,
    importData
  } = useFilmTVData(uid);
  
  const { searchMovies, searchTVShows, searchPeople, getCreatorFilmography } = useTMDB();

  const [activeCategory, setActiveCategory] = useState('movies'); // 'movies', 'tv', 'creators'
  const [activeView, setActiveView] = useState('watchlist'); // 'watchlist', 'history', 'search', 'new_releases', 'requests'
  const [newReleasesMonths, setNewReleasesMonths] = useState(3);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [expandedCreatorId, setExpandedCreatorId] = useState(null);
  const [addingCreatorId, setAddingCreatorId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (activeCategory === 'creators') {
      if (activeView !== 'watchlist' && activeView !== 'search') setActiveView('watchlist');
    }
  }, [activeCategory]);

  useEffect(() => {
    if (activeView === 'search' && searchQuery.trim().length > 2) {
      const delayDebounceFn = setTimeout(async () => {
        setIsSearching(true);
        try {
          if (activeCategory === 'movies') setSearchResults(await searchMovies(searchQuery));
          else if (activeCategory === 'tv') setSearchResults(await searchTVShows(searchQuery));
          else if (activeCategory === 'creators') setSearchResults(await searchPeople(searchQuery));
        } catch (err) {
          console.error(err);
        } finally {
          setIsSearching(false);
        }
      }, 500);
      return () => clearTimeout(delayDebounceFn);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, activeView, activeCategory, searchMovies, searchTVShows, searchPeople]);

  const handleAddResult = async (item) => {
    if (activeCategory === 'movies') {
      const exists = movies.find(m => m.tmdbId === item.id);
      if (exists) return;
      await addMovie({
        tmdbId: item.id,
        title: item.title,
        poster_path: item.poster_path,
        release_date: item.release_date,
        overview: item.overview,
      });
    } else if (activeCategory === 'tv') {
      const exists = tvShows.find(t => t.tmdbId === item.id);
      if (exists) return;
      await addTvShow({
        tmdbId: item.id,
        name: item.name,
        poster_path: item.poster_path,
        first_air_date: item.first_air_date,
        overview: item.overview,
      });
    } else if (activeCategory === 'creators') {
      const exists = creators.find(c => c.id === item.id);
      if (exists) return;
      
      setAddingCreatorId(item.id);
      const filmography = await getCreatorFilmography(item.id);
      await addCreator({
        id: item.id,
        name: item.name,
        profile_path: item.profile_path,
        movies: filmography
      });
      setAddingCreatorId(null);
    }
    setActiveView('watchlist');
    setSearchQuery('');
  };

  const toggleWatched = async (id, isTv, currentSeen) => {
    const data = { seen: !currentSeen, toWatch: currentSeen };
    if (!currentSeen) data.watchedDate = new Date().toISOString();
    if (isTv) await updateTvShow(id, data);
    else await updateMovie(id, data);
  };

  const toggleFlagged = async (id, isTv, currentFlag) => {
    if (isTv) await updateTvShow(id, { flagged: !currentFlag });
    else await updateMovie(id, { flagged: !currentFlag });
  };

  const updateNotes = async (id, isTv, text) => {
    if (isTv) await updateTvShow(id, { notes: text });
    else await updateMovie(id, { notes: text });
  };

  const movePosition = async (id, isTv, currentPos, direction) => {
    const items = isTv ? tvShows : movies;
    const sorted = [...items].filter(i => i.toWatch).sort((a,b) => (a.position||0) - (b.position||0));
    const idx = sorted.findIndex(i => (i.id||i.tmdbId) === id);
    if (idx === -1) return;
    
    if (direction === 'up' && idx > 0) {
      const swap = sorted[idx-1];
      // eslint-disable-next-line react-hooks/purity
      const newPos = swap.position || Date.now();
      // eslint-disable-next-line react-hooks/purity
      const myPos = currentPos || Date.now();
      if (isTv) { await updateTvShow(id, { position: newPos }); await updateTvShow(swap.id, { position: myPos }); }
      else { await updateMovie(id, { position: newPos }); await updateMovie(swap.id, { position: myPos }); }
    } else if (direction === 'down' && idx < sorted.length - 1) {
      const swap = sorted[idx+1];
      // eslint-disable-next-line react-hooks/purity
      const newPos = swap.position || Date.now();
      // eslint-disable-next-line react-hooks/purity
      const myPos = currentPos || Date.now();
      if (isTv) { await updateTvShow(id, { position: newPos }); await updateTvShow(swap.id, { position: myPos }); }
      else { await updateMovie(id, { position: newPos }); await updateMovie(swap.id, { position: myPos }); }
    }
  };

  const toggleCreatorMovieWatched = async (creatorId, movieId, currentSeen) => {
    const creator = creators.find(c => c.id === creatorId);
    if (!creator) return;
    const updatedMovies = creator.movies.map(m => {
      if (m.tmdbId === movieId) return { ...m, seen: !currentSeen, toWatch: currentSeen, watchedDate: !currentSeen ? new Date().toISOString() : null };
      return m;
    });
    await updateCreator(creatorId, { movies: updatedMovies });
  };

  const toggleCreatorMovieInterest = async (creatorId, movieId, currentNotInterested) => {
    const creator = creators.find(c => c.id === creatorId);
    if (!creator) return;
    const updatedMovies = creator.movies.map(m => {
      if (m.tmdbId === movieId) return { ...m, notInterested: !currentNotInterested };
      return m;
    });
    await updateCreator(creatorId, { movies: updatedMovies });
  };

  const handleRefreshAll = async () => {
    setIsRefreshing(true);
    for (const c of creators) {
      const freshFilmography = await getCreatorFilmography(c.id);
      // Merge keeping user states (seen, notInterested, etc)
      const merged = freshFilmography.map(fresh => {
        const existing = c.movies?.find(m => m.tmdbId === fresh.tmdbId);
        if (existing) return { ...fresh, seen: existing.seen, toWatch: existing.toWatch, notInterested: existing.notInterested, watchedDate: existing.watchedDate, notes: existing.notes };
        return fresh;
      });
      await updateCreator(c.id, { movies: merged });
    }
    setIsRefreshing(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const json = JSON.parse(e.target.result);
        await importData(json);
        alert('Import successful!');
      } catch (err) {
        console.error(err);
        alert('Failed to import JSON data.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const newReleases = useMemo(() => {
    const pastDate = new Date();
    pastDate.setMonth(pastDate.getMonth() - newReleasesMonths);
    const today = new Date();
    
    if (activeCategory === 'movies') {
      const allMoviesMap = new Map();
      movies.forEach(m => {
        if (m.release_date) {
          const d = new Date(m.release_date);
          if (d >= pastDate && d <= today && !m.seen && !m.notInterested) allMoviesMap.set(m.tmdbId, m);
        }
      });
      creators.forEach(c => {
        c.movies.forEach(m => {
          if (m.release_date) {
            const d = new Date(m.release_date);
            if (d >= pastDate && d <= today && !m.seen && !m.notInterested) {
              allMoviesMap.set(m.tmdbId, { ...m, _creatorName: c.name });
            }
          }
        });
      });
      return Array.from(allMoviesMap.values()).sort((a, b) => new Date(b.release_date) - new Date(a.release_date));
    } else if (activeCategory === 'tv') {
      return tvShows.filter(t => {
        if (!t.first_air_date || t.seen || t.notInterested) return false;
        const d = new Date(t.first_air_date);
        return d >= pastDate && d <= today;
      }).sort((a, b) => new Date(b.first_air_date) - new Date(a.first_air_date));
    }
    return [];
  }, [movies, creators, tvShows, activeCategory, newReleasesMonths]);

  const getFilteredItems = () => {
    if (activeCategory === 'creators') return creators;
    const list = activeCategory === 'movies' ? movies : tvShows;
    if (activeView === 'watchlist') return list.filter(i => i.toWatch && !i.flagged);
    if (activeView === 'history') return list.filter(i => i.seen);
    if (activeView === 'requests') return list.filter(i => i.flagged);
    if (activeView === 'new_releases') return newReleases;
    return [];
  };

  const renderItemCard = (item, isWatchlistMode = false) => {
    const title = item.title || item.name;
    const date = item.release_date || item.first_air_date;
    const year = date ? new Date(date).getFullYear() : 'TBA';
    const isTv = activeCategory === 'tv';

    return (
      <div key={item.id || item.tmdbId} className="flex gap-4 p-4 bg-white border border-zinc-200 rounded-xl shadow-sm hover:shadow-md transition-shadow group relative">
        <div className="w-16 h-24 bg-zinc-100 rounded bg-cover bg-center shrink-0 border border-zinc-200" 
             style={{ backgroundImage: item.poster_path ? `url(https://image.tmdb.org/t/p/w200${item.poster_path})` : 'none' }}>
          {!item.poster_path && <div className="w-full h-full flex items-center justify-center text-zinc-300"><Film size={24} /></div>}
        </div>
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <div>
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-bold text-zinc-900 truncate">{title}</h3>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {isWatchlistMode && (
                  <>
                    <button onClick={() => movePosition(item.id, isTv, item.position, 'up')} className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-900"><ArrowUp size={14}/></button>
                    <button onClick={() => movePosition(item.id, isTv, item.position, 'down')} className="p-1 hover:bg-zinc-100 rounded text-zinc-400 hover:text-zinc-900"><ArrowDown size={14}/></button>
                  </>
                )}
                <button 
                  onClick={() => toggleFlagged(item.id || item.tmdbId, isTv, item.flagged)}
                  className={`p-1.5 rounded-full ${item.flagged ? 'text-red-500 bg-red-50 hover:bg-red-100' : 'text-zinc-400 bg-zinc-50 hover:bg-zinc-100 hover:text-red-500'}`}
                  title={item.flagged ? 'Unflag' : 'Flag for later'}
                >
                  <Flag size={14} strokeWidth={item.flagged ? 3 : 2} />
                </button>
                <button 
                  onClick={() => toggleWatched(item.id || item.tmdbId, isTv, item.seen)}
                  className={`p-1.5 rounded-full ${item.seen ? 'text-green-600 bg-green-50 hover:bg-green-100' : 'text-zinc-400 bg-zinc-50 hover:bg-zinc-100 hover:text-zinc-900'}`}
                  title={item.seen ? 'Mark as Unwatched' : 'Mark as Watched'}
                >
                  <Check size={14} strokeWidth={3} />
                </button>
                <button 
                  onClick={() => isTv ? deleteTvShow(item.id) : deleteMovie(item.id)}
                  className="p-1.5 text-red-400 bg-red-50 hover:bg-red-100 hover:text-red-600 rounded-full"
                  title="Remove"
                >
                  <X size={14} strokeWidth={3} />
                </button>
              </div>
            </div>
            <p className="text-xs font-black tracking-widest text-zinc-400 uppercase mt-0.5">
              {year} {item._creatorName && `• By ${item._creatorName}`}
            </p>
            
            {activeView === 'history' && (
              <div className="mt-2 text-xs">
                {item.watchedDate && <p className="text-zinc-400">Watched: {new Date(item.watchedDate).toLocaleDateString()}</p>}
                <input 
                  type="text" 
                  className="mt-1 w-full text-xs p-1.5 bg-zinc-50 border border-zinc-200 rounded focus:outline-none focus:ring-1 focus:ring-zinc-400"
                  placeholder="Add notes..."
                  value={item.notes || ''}
                  onChange={(e) => updateNotes(item.id || item.tmdbId, isTv, e.target.value)}
                />
              </div>
            )}
            {activeView !== 'history' && <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{item.overview}</p>}
          </div>
        </div>
      </div>
    );
  };

  const renderCreatorCard = (creator) => {
    const isExpanded = expandedCreatorId === creator.id;
    const sortedMovies = [...creator.movies].sort((a, b) => {
      if (!a.release_date) return 1;
      if (!b.release_date) return -1;
      return new Date(b.release_date) - new Date(a.release_date);
    });

    return (
      <div key={creator.id} className="bg-white border border-zinc-200 rounded-xl shadow-sm mb-4 overflow-hidden">
        <div className="flex items-center justify-between p-4 cursor-pointer hover:bg-zinc-50 transition-colors" onClick={() => setExpandedCreatorId(isExpanded ? null : creator.id)}>
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-zinc-100 rounded-full bg-cover bg-center border border-zinc-200" 
                 style={{ backgroundImage: creator.profile_path ? `url(https://image.tmdb.org/t/p/w200${creator.profile_path})` : 'none' }}>
              {!creator.profile_path && <Users size={20} className="m-auto mt-3 text-zinc-300" />}
            </div>
            <div>
              <h3 className="font-bold text-zinc-900">{creator.name}</h3>
              <p className="text-xs font-black tracking-widest text-zinc-400 uppercase mt-0.5">{creator.movies.length} Films</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={(e) => { e.stopPropagation(); deleteCreator(creator.id); }}
              className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
              title="Remove Creator"
            >
              <Trash2 size={16} />
            </button>
            {isExpanded ? <ChevronUp size={20} className="text-zinc-400" /> : <ChevronDown size={20} className="text-zinc-400" />}
          </div>
        </div>
        
        {isExpanded && (
          <div className="border-t border-zinc-100 bg-zinc-50 p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {sortedMovies.map(movie => (
                <div key={movie.tmdbId} className={`flex gap-3 bg-white p-3 rounded-lg border shadow-sm group ${movie.notInterested ? 'opacity-50 border-zinc-200' : 'border-zinc-200'}`}>
                   <div className="w-12 h-16 bg-zinc-100 rounded bg-cover bg-center shrink-0" 
                        style={{ backgroundImage: movie.poster_path ? `url(https://image.tmdb.org/t/p/w200${movie.poster_path})` : 'none' }} />
                   <div className="flex-1 min-w-0 flex flex-col justify-center">
                     <div className="flex justify-between items-start">
                        <h4 className={`text-sm font-bold truncate pr-2 ${movie.notInterested ? 'text-zinc-400 line-through' : 'text-zinc-900'}`}>{movie.title}</h4>
                        <div className="flex gap-1 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button 
                            onClick={() => toggleCreatorMovieInterest(creator.id, movie.tmdbId, movie.notInterested)}
                            className={`p-1 rounded-full ${movie.notInterested ? 'text-orange-500 bg-orange-50' : 'text-zinc-300 hover:text-orange-500'}`}
                            title="Not Interested"
                          >
                            <Ban size={14} />
                          </button>
                          <button 
                            onClick={() => toggleCreatorMovieWatched(creator.id, movie.tmdbId, movie.seen)}
                            className={`p-1 rounded-full ${movie.seen ? 'text-green-600 bg-green-50' : 'text-zinc-300 hover:text-green-600'}`}
                            title="Mark Watched"
                          >
                            <Check size={14} strokeWidth={3} />
                          </button>
                        </div>
                     </div>
                     <p className="text-xs text-zinc-500 font-medium">{movie.year || 'TBA'} • {movie.roles.join(', ')}</p>
                   </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderSearchResultCard = (item) => {
    const isCreator = activeCategory === 'creators';
    const title = item.title || item.name;
    const date = item.release_date || item.first_air_date;
    const year = date ? new Date(date).getFullYear() : (isCreator ? 'Person' : 'TBA');
    const imagePath = isCreator ? item.profile_path : item.poster_path;
    
    let isAdded = false;
    if (activeCategory === 'movies') isAdded = movies.some(m => m.tmdbId === item.id);
    else if (activeCategory === 'tv') isAdded = tvShows.some(t => t.tmdbId === item.id);
    else if (activeCategory === 'creators') isAdded = creators.some(c => c.id === item.id);

    const isAdding = addingCreatorId === item.id;

    return (
      <div key={item.id} className="flex gap-4 p-4 bg-white border border-zinc-200 rounded-xl shadow-sm hover:shadow-md transition-shadow">
        <div className={`w-16 h-24 bg-zinc-100 rounded bg-cover bg-center shrink-0 border border-zinc-200 ${isCreator ? 'rounded-full w-16 h-16' : ''}`} 
             style={{ backgroundImage: imagePath ? `url(https://image.tmdb.org/t/p/w200${imagePath})` : 'none' }}>
          {!imagePath && <div className="w-full h-full flex items-center justify-center text-zinc-300">{isCreator ? <Users size={24} /> : <Film size={24} />}</div>}
        </div>
        <div className="flex-1 flex flex-col justify-between min-w-0">
          <div>
            <h3 className="font-bold text-zinc-900 truncate">{title}</h3>
            <p className="text-xs font-black tracking-widest text-zinc-400 uppercase mt-0.5">{year}</p>
            {!isCreator && <p className="text-sm text-zinc-500 mt-2 line-clamp-2">{item.overview}</p>}
          </div>
          <div className="flex justify-end mt-2">
            <button 
              onClick={() => handleAddResult(item)}
              disabled={isAdded || isAdding}
              className={`flex items-center gap-1.5 px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded transition-colors ${
                isAdded ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed' : 'bg-zinc-900 text-white hover:bg-black'
              }`}
            >
              {isAdding ? <><Loader2 size={12} className="animate-spin" /> Adding...</> : 
               isAdded ? <><Check size={12} strokeWidth={3} /> Added</> : 
               <><Plus size={12} strokeWidth={3} /> Add</>}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-zinc-50">
      <input type="file" accept=".json" ref={fileInputRef} className="hidden" onChange={handleImport} />
      
      <header className="bg-white border-b border-zinc-200 shrink-0 relative z-10 shadow-sm">
        <div className="h-14 px-6 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Film size={20} className="text-zinc-900" />
            <h1 className="text-sm font-black uppercase tracking-widest text-zinc-900">Film & TV Tracker</h1>
          </div>
          <div className="flex items-center gap-1 bg-zinc-100 p-1 rounded-lg">
            <button
              onClick={() => setActiveCategory('movies')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase tracking-widest rounded transition-all ${
                activeCategory === 'movies' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <Film size={14} /> Films
            </button>
            <button
              onClick={() => setActiveCategory('tv')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase tracking-widest rounded transition-all ${
                activeCategory === 'tv' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <Tv size={14} /> TV Shows
            </button>
            <button
              onClick={() => setActiveCategory('creators')}
              className={`flex items-center gap-2 px-3 py-1.5 text-xs font-black uppercase tracking-widest rounded transition-all ${
                activeCategory === 'creators' ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'
              }`}
            >
              <Users size={14} /> Creators
            </button>
          </div>
        </div>
        
        {activeView !== 'search' && (
          <div className="px-6 flex items-center justify-between border-t border-zinc-100">
            <div className="flex items-center gap-4">
              {activeCategory === 'creators' ? (
                <TabButton active={activeView === 'watchlist'} onClick={() => setActiveView('watchlist')} icon={Users} label="Following" />
              ) : (
                <>
                  <TabButton active={activeView === 'watchlist'} onClick={() => setActiveView('watchlist')} icon={Clock} label="Watchlist" />
                  <TabButton active={activeView === 'new_releases'} onClick={() => setActiveView('new_releases')} icon={CalendarDays} label="New Releases" />
                  <TabButton active={activeView === 'requests'} onClick={() => setActiveView('requests')} icon={Flag} label="Requests" iconColor="text-red-500" />
                  <TabButton active={activeView === 'history'} onClick={() => setActiveView('history')} icon={Check} label="History" />
                </>
              )}
            </div>
            <div className="flex items-center gap-2 py-2">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded" title="Import JSON Backup"
              >
                <Upload size={16} />
              </button>
              {activeCategory === 'creators' && (
                <button 
                  onClick={handleRefreshAll}
                  disabled={isRefreshing}
                  className={`p-1.5 text-zinc-400 hover:text-zinc-900 hover:bg-zinc-100 rounded ${isRefreshing ? 'animate-spin' : ''}`} title="Refresh Creator Data"
                >
                  <RefreshCw size={16} />
                </button>
              )}
              <button 
                onClick={() => { setActiveView('search'); setSearchQuery(''); }}
                className="flex items-center gap-2 px-4 py-1.5 bg-zinc-900 text-white text-[10px] font-black uppercase tracking-widest rounded shadow-md hover:bg-black transition-all active:scale-95 ml-2"
              >
                <Plus size={14} /> Add {activeCategory === 'creators' ? 'Creator' : 'Title'}
              </button>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        {activeView === 'new_releases' && (
          <div className="max-w-5xl mx-auto flex justify-center gap-2 mb-6">
            <button onClick={() => setNewReleasesMonths(1)} className={`px-4 py-1 text-xs font-bold rounded-full ${newReleasesMonths === 1 ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'}`}>1 Month</button>
            <button onClick={() => setNewReleasesMonths(3)} className={`px-4 py-1 text-xs font-bold rounded-full ${newReleasesMonths === 3 ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'}`}>3 Months</button>
            <button onClick={() => setNewReleasesMonths(6)} className={`px-4 py-1 text-xs font-bold rounded-full ${newReleasesMonths === 6 ? 'bg-zinc-900 text-white' : 'bg-zinc-200 text-zinc-600 hover:bg-zinc-300'}`}>6 Months</button>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 text-zinc-300 animate-spin" />
          </div>
        ) : activeView === 'search' ? (
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <button 
                onClick={() => setActiveView('watchlist')}
                className="p-2 text-zinc-400 hover:text-zinc-900 transition-colors"
              >
                <ArrowLeft size={20} />
              </button>
              <div className="flex-1 relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder={`Search for a ${activeCategory === 'movies' ? 'movie' : activeCategory === 'tv' ? 'TV show' : 'person'}...`}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-white border border-zinc-200 rounded-xl pl-11 pr-4 py-3 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zinc-900 transition-shadow"
                  autoFocus
                />
                {isSearching && (
                  <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 animate-spin" />
                )}
              </div>
            </div>

            {searchResults.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {searchResults.map(renderSearchResultCard)}
              </div>
            ) : searchQuery.trim().length > 2 && !isSearching ? (
              <div className="text-center py-20 text-zinc-400">
                <p className="text-sm font-medium">No results found for &quot;{searchQuery}&quot;</p>
              </div>
            ) : (
              <div className="text-center py-20 text-zinc-300">
                <Search size={48} strokeWidth={1} className="mx-auto mb-4 opacity-50" />
                <p className="text-xs font-black tracking-widest uppercase">Type to search</p>
              </div>
            )}
          </div>
        ) : (
          <div className="max-w-5xl mx-auto">
            {activeCategory === 'creators' ? (
              creators.length > 0 ? (
                <div className="max-w-3xl mx-auto">
                  {creators.map(renderCreatorCard)}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-32 text-zinc-300">
                  <Users size={48} strokeWidth={1} />
                  <span className="text-[10px] font-black uppercase tracking-widest mt-4">You aren&apos;t following any creators</span>
                </div>
              )
            ) : getFilteredItems().length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {getFilteredItems().map(i => renderItemCard(i, activeView === 'watchlist'))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-32 text-zinc-300">
                {activeCategory === 'movies' ? <Film size={48} strokeWidth={1} /> : <Tv size={48} strokeWidth={1} />}
                <span className="text-[10px] font-black uppercase tracking-widest mt-4">
                  {activeView === 'watchlist' ? 'Your watchlist is empty' : 
                   activeView === 'requests' ? 'No flagged requests' : 
                   activeView === 'new_releases' ? 'No recent releases' : 'Your history is empty'}
                </span>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
